import type { Locale } from '@app/constants/Locales'
import { DEFAULT_LOCALE } from '@app/constants/Locales'

import { reportSpawnFailure } from '@app/utils/errorReport/reportSpawnFailure'

import { spawn } from '@app/packages/disposable/spawn'

import { mergeIntlMessages } from '@app/store/slices/intl'
import type { Store } from '@app/store/store'

const FETCH_DEBOUNCE_MS = 16

export class IntlChunkLoader {
  private known = new Set<string>()
  private pending = new Set<string>()
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private flushChain: Promise<void> = Promise.resolve()
  private enabled = false

  constructor(
    private store: Store,
    private locale: Locale
  ) {}

  start() {
    return spawn(stack => {
      if (this.locale === DEFAULT_LOCALE) return

      const record = (id: string) => this.observe(String(id))
      // Each `import()` call site announces the module it is about to load, which is how a
      // navigation is known to have pulled in messages this locale has not fetched yet.
      const usage = globalThis.__kidsout_module_usage__
      if (!usage) throw new Error('IntlChunkLoader: nothing announces module loads, so a navigation would keep the messages it started with')

      this.enabled = true
      usage.listeners.add(record)
      stack.defer(() => usage.listeners.delete(record))
      stack.defer(() => {
        this.enabled = false
        if (this.flushTimer) {
          clearTimeout(this.flushTimer)
          this.flushTimer = null
        }
      })
    }, reportSpawnFailure('IntlChunkLoader'))
  }

  async flushNow() {
    if (!this.enabled) return
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
    await this.flush()
  }

  private observe(moduleId: string) {
    if (this.known.has(moduleId)) return
    this.known.add(moduleId)
    this.pending.add(moduleId)
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => {
        this.flushTimer = null
        void this.flush()
      }, FETCH_DEBOUNCE_MS)
    }
  }

  private flush(): Promise<void> {
    this.flushChain = this.flushChain.then(() => this.doFlush())
    return this.flushChain
  }

  private async doFlush() {
    if (this.pending.size === 0) return
    const batch = Array.from(this.pending)
    this.pending.clear()
    try {
      const resp = await fetch(`/internal/translations/${this.locale}/modules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleIds: batch }),
      })
      if (!resp.ok) return
      const messages = (await resp.json()) as Record<string, string>
      this.store.dispatch(mergeIntlMessages(messages))
    } catch (err) {
      console.error('IntlChunkLoader: failed to fetch translations', err)
    }
  }
}
